import { useState, useEffect } from 'react';
import { GpsDevice } from '@/types/gps';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChipOperatorSelector } from './ChipOperatorSelector';
import { useGpsVehicles } from '@/hooks/useGpsVehicles';
import { Car, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface DeviceFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  device?: GpsDevice;
  onSubmit: (data: Partial<GpsDevice>) => void;
  isLoading?: boolean;
}

export function DeviceFormSheet({
  open,
  onOpenChange,
  companyId,
  device,
  onSubmit,
  isLoading
}: DeviceFormSheetProps) {
  const [formData, setFormData] = useState({
    imei_or_uid: '',
    code: '',
    model: 'ST_901',
    sim_iccid: '',
    chip_operator: null as 'tim' | 'claro' | 'vivo' | 'm2m' | null,
    vehicle_id: '',
  });

  const { data: vehicles } = useGpsVehicles(companyId);

  useEffect(() => {
    if (device) {
      setFormData({
        imei_or_uid: device.imei_or_uid || '',
        code: device.code || '',
        model: device.model || 'ST_901',
        sim_iccid: device.sim_iccid || '',
        chip_operator: device.chip_operator || null,
        vehicle_id: device.vehicle_id || '',
      });
    } else {
      setFormData({
        imei_or_uid: '',
        code: '',
        model: 'ST_901',
        sim_iccid: '',
        chip_operator: null,
        vehicle_id: '',
      });
    }
  }, [device, open]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!formData.code || !formData.imei_or_uid) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    if (!formData.chip_operator) {
      toast.error('Selecione a operadora do chip');
      return;
    }

    // Validar ICCID com operadora
    if (formData.chip_operator !== 'm2m') {
      const prefixes: Record<string, string> = {
        tim: '8955',
        claro: '8931',
        vivo: '8932'
      };
      
      const expectedPrefix = prefixes[formData.chip_operator];
      if (!formData.sim_iccid.startsWith(expectedPrefix)) {
        toast.error(`ICCID deve começar com ${expectedPrefix} para ${formData.chip_operator.toUpperCase()}`);
        return;
      }

      if (formData.sim_iccid.length !== 20) {
        toast.error('ICCID deve ter exatamente 20 dígitos');
        return;
      }
    }
    
    onSubmit({
      imei_or_uid: formData.imei_or_uid,
      code: formData.code,
      model: formData.model,
      sim_iccid: formData.sim_iccid || null,
      chip_operator: formData.chip_operator,
      vehicle_id: formData.vehicle_id || null,
      company_id: companyId,
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[650px] p-6">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle>
            {device ? 'Editar' : 'Novo'} Equipamento GPS
          </SheetTitle>
          <SheetDescription>
            Preencha os dados do rastreador GPS
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] py-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Seção 1: Identificação do Equipamento */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Identificação
              </h3>
              
              <div className="space-y-2">
                <Label htmlFor="code">Código IKT *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="IKT-12345"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="imei">IMEI / UID *</Label>
                <Input
                  id="imei"
                  value={formData.imei_or_uid}
                  onChange={(e) => setFormData({ ...formData, imei_or_uid: e.target.value })}
                  placeholder="123456789012345"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="model">Modelo *</Label>
                <Select 
                  value={formData.model} 
                  onValueChange={(value) => setFormData({ ...formData, model: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o modelo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ST_901">ST-901 (Sinotrack)</SelectItem>
                    <SelectItem value="ST_902">ST-902 (Sinotrack)</SelectItem>
                    <SelectItem value="ST_906">ST-906 (Sinotrack)</SelectItem>
                    <SelectItem value="ST_915">ST-915 (Sinotrack)</SelectItem>
                    <SelectItem value="ST_940">ST-940 (Sinotrack)</SelectItem>
                    <SelectItem value="TK103">TK103 (Sinotrack)</SelectItem>
                    <SelectItem value="TK116">TK116 (Sinotrack)</SelectItem>
                    <SelectItem value="TK119">TK119 (Sinotrack)</SelectItem>
                    <SelectItem value="TK303">TK303 (Sinotrack)</SelectItem>
                    <SelectItem value="TK905">TK905 (Sinotrack)</SelectItem>
                    <SelectItem value="ST_907">ST-907 (Sinotrack)</SelectItem>
                    <SelectItem value="FMB130">FMB130 (Teltonika)</SelectItem>
                    <SelectItem value="FMB910">FMB910 (Teltonika)</SelectItem>
                    <SelectItem value="FMB920">FMB920 (Teltonika)</SelectItem>
                    <SelectItem value="CRX3_MINI">CRX3 Mini (Concox)</SelectItem>
                    <SelectItem value="RST_MINI">RST Mini (RST)</SelectItem>
                    <SelectItem value="VL03">VL03 (Vigicar)</SelectItem>
                    <SelectItem value="OUTRO">Outro modelo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Seção 2: Chip M2M */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Chip M2M
              </h3>
              
              <ChipOperatorSelector
                value={formData.chip_operator}
                onChange={(operator) => {
                  const prefixes: Record<string, string> = {
                    tim: '8955',
                    claro: '8931',
                    vivo: '8932',
                    m2m: ''
                  };
                  
                  setFormData({ 
                    ...formData, 
                    chip_operator: operator,
                    sim_iccid: operator && operator !== 'm2m' ? prefixes[operator] : ''
                  });
                }}
              />

              <div className="space-y-2">
                <Label htmlFor="sim">ICCID do Chip *</Label>
                <Input
                  id="sim"
                  value={formData.sim_iccid}
                  onChange={(e) => setFormData({ ...formData, sim_iccid: e.target.value })}
                  placeholder="89551234567890123456"
                  maxLength={20}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Número completo do chip SIM (20 dígitos)
                </p>
              </div>
            </div>

            {/* Seção 3: Veículo */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Instalação
              </h3>
              
              <div className="space-y-2">
                <Label htmlFor="vehicle">Veículo Associado</Label>
                <Select 
                  value={formData.vehicle_id} 
                  onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Nenhum (instalar depois)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-muted-foreground">Sem veículo</span>
                    </SelectItem>
                    {vehicles?.map((vehicle: any) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        <div className="flex items-center gap-2">
                          <Car className="h-4 w-4" />
                          {vehicle.plate} - {vehicle.model}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Pode ser associado posteriormente
                </p>
              </div>
            </div>

          </form>
        </ScrollArea>

        <SheetFooter className="py-4 border-t bg-background">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleSubmit()}
            disabled={isLoading || !formData.imei_or_uid || !formData.chip_operator}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              <>{device ? 'Atualizar' : 'Criar'} Equipamento</>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
